import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Target, TrendingUp, Brain, Sparkles, MessageSquare, ArrowRight, Zap, Moon, Activity, Phone, Mail, Calendar, Download } from 'lucide-react';
import ScheduleModal from '../components/ScheduleModal';
import ContactModal from '../components/ContactModal';
import LearnMoreModal from '../components/LearnMoreModal';
import FutureYouPreview from '../components/FutureYouPreview';

const ResultsPage = () => {
  const { leadId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [forecastData, setForecastData] = useState(location.state?.forecastData || null);
  const [quizResponses, setQuizResponses] = useState(location.state?.quizResponses || null);
  const [profile, setProfile] = useState(location.state?.aiProfile || null);
  
  // Modal states
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showLearnMoreModal, setShowLearnMoreModal] = useState(false);
  const [learnMoreTopic, setLearnMoreTopic] = useState('general');

  // Helper to open learn more modal with specific topic
  const openLearnMore = (topic) => {
    setLearnMoreTopic(topic);
    setShowLearnMoreModal(true);
  };

  useEffect(() => {
    if (!forecastData) {
      // Could fetch from API if needed
      navigate('/');
    }
    
    // Also set profile from forecastData if available
    if (forecastData?.ai_profile && !profile) {
      setProfile(forecastData.ai_profile);
    }
  }, [forecastData, navigate, profile]);

  if (!forecastData) return null;

  // Determine if user should see Sermorelin recommendation
  const shouldShowSermorelin = () => {
    if (!quizResponses) return false;
    
    const challenge = quizResponses.biggest_challenge?.toLowerCase() || '';
    const futureInterests = quizResponses.future_interests || [];
    
    // Show Sermorelin if user has:
    // - Low energy challenge
    // - Recovery-related challenges
    // - Busy lifestyle
    // - Weight plateau
    // - Interest in energy/metabolic support
    const relevantChallenges = ['low energy', 'weight plateau', 'busy lifestyle', 'consistency'];
    const hasRelevantChallenge = relevantChallenges.some(c => challenge.includes(c));
    
    const hasMetabolicInterest = futureInterests.some(interest => 
      interest.toLowerCase().includes('energy') || 
      interest.toLowerCase().includes('nad')
    );
    
    return hasRelevantChallenge || hasMetabolicInterest;
  };

  const getScoreBadge = (score) => {
    const colors = {
      hot: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      warm: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      cold: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    };
    return colors[score] || colors.cold;
  };

  const getUserFacingScore = (score) => {
    const labels = {
      hot: 'Strong Candidate',
      warm: 'Good Candidate',
      cold: 'Additional Review Recommended'
    };
    return labels[score] || labels.cold;
  };

  // Determine medication recommendations based on quiz responses
  const getMedicationRecommendation = () => {
    if (!quizResponses) return null;

    const challenge = quizResponses.biggest_challenge?.toLowerCase() || '';
    const priorGLP1 = quizResponses.prior_glp1?.toLowerCase() || '';
    const medicationInterest = quizResponses.medication_interest?.toLowerCase() || '';
    const weightDiff = parseInt(quizResponses.current_weight) - parseInt(quizResponses.goal_weight);
    
    let primary = 'Tirzepatide';
    let alternative = 'Semaglutide';
    let reasoning = '';

    // If user explicitly expressed interest, honor that
    if (medicationInterest.includes('tirzepatide')) {
      primary = 'Tirzepatide';
      alternative = 'Semaglutide';
      reasoning = 'Based on your expressed interest in Tirzepatide and your profile, this dual-action medication may provide comprehensive support for your weight loss goals. It targets both GLP-1 and GIP receptors, which some individuals find beneficial for appetite control and metabolic support.';
    } else if (medicationInterest.includes('semaglutide')) {
      primary = 'Semaglutide';
      alternative = 'Tirzepatide';
      reasoning = 'Based on your expressed interest in Semaglutide and your profile, this well-established GLP-1 medication may offer reliable support for appetite regulation and weight management. Many individuals have found success with this option.';
    } else {
      // Make recommendation based on profile
      
      // Tirzepatide may be better for:
      // - Larger weight loss goals (40+ lbs)
      // - Appetite control challenges
      // - Weight plateaus
      // - No prior GLP-1 use (newer option)
      
      if (weightDiff >= 40 || challenge.includes('plateau') || challenge.includes('appetite')) {
        primary = 'Tirzepatide';
        alternative = 'Semaglutide';
        reasoning = 'Your profile suggests Tirzepatide may be a strong match. As a dual-action medication targeting both GLP-1 and GIP receptors, it may provide enhanced support for ';
        
        if (challenge.includes('appetite')) {
          reasoning += 'appetite control and hunger regulation. ';
        } else if (challenge.includes('plateau')) {
          reasoning += 'breaking through weight plateaus with a different metabolic approach. ';
        } else {
          reasoning += 'achieving your weight loss goals with comprehensive metabolic support. ';
        }
        
        reasoning += 'Semaglutide remains an excellent alternative with a well-established track record.';
      } else {
        // Semaglutide may be better for:
        // - Moderate weight loss goals
        // - Prior GLP-1 experience
        // - Preference for established options
        
        primary = 'Semaglutide';
        alternative = 'Tirzepatide';
        reasoning = 'Your profile suggests Semaglutide may be an excellent starting point. ';
        
        if (priorGLP1.includes('currently') || priorGLP1.includes('past')) {
          reasoning += 'With your prior GLP-1 experience, this well-established option provides reliable support for appetite regulation and weight management. ';
        } else {
          reasoning += 'This well-established GLP-1 medication has helped many individuals achieve their weight loss goals with proven appetite control support. ';
        }
        
        reasoning += 'Tirzepatide is also available as an alternative if you\'re looking for dual-action support.';
      }
    }

    return { primary, alternative, reasoning };
  };

  const medicationRec = getMedicationRecommendation();

  return (
    <div className="min-h-screen bg-[#020817] text-foreground">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-24 pb-16">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#22D3EE] opacity-10 blur-[100px] rounded-full"></div>
        
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm">
              <Sparkles className="h-4 w-4 text-[#22D3EE]" />
              <span className="text-[#22D3EE] text-sm font-medium tracking-wide">YOUR AI FORECAST IS READY</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-4">
              Your Personalized <span className="text-[#22D3EE]">Forecast</span>
            </h1>
            
            <div className="inline-flex items-center gap-2 mt-4">
              <span className="text-slate-400 text-sm">AI Readiness Insight:</span>
              <span className={`px-4 py-1 rounded-full text-sm font-bold border ${getScoreBadge(forecastData.lead_score)}`}>
                {getUserFacingScore(forecastData.lead_score)}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Forecast Cards */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 space-y-6">
          {/* Goal Profile */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="backdrop-blur-xl bg-slate-950/40 border border-white/10 rounded-2xl p-8"
            data-testid="goal-profile-card"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#22D3EE]/10 flex items-center justify-center flex-shrink-0">
                <Target className="h-6 w-6 text-[#22D3EE]" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold mb-3">Your Goal Profile</h2>
                <p className="text-slate-300 leading-relaxed">{forecastData.goal_profile}</p>
              </div>
            </div>
          </motion.div>

          {/* Challenge Pattern */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="backdrop-blur-xl bg-slate-950/40 border border-white/10 rounded-2xl p-8"
            data-testid="challenge-pattern-card"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#22D3EE]/10 flex items-center justify-center flex-shrink-0">
                <Brain className="h-6 w-6 text-[#22D3EE]" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold mb-3">Your Challenge Pattern</h2>
                <p className="text-slate-300 leading-relaxed">{forecastData.challenge_pattern}</p>
              </div>
            </div>
          </motion.div>

          {/* AI Forecast */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="backdrop-blur-xl bg-slate-950/40 border border-[#22D3EE]/30 rounded-2xl p-8 shadow-[0_0_30px_rgba(34,211,238,0.2)]"
            data-testid="forecast-card"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#22D3EE]/20 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="h-6 w-6 text-[#22D3EE]" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold mb-3">Your AI Weight Loss Forecast</h2>
                <p className="text-slate-300 leading-relaxed">{forecastData.forecast_summary}</p>
              </div>
            </div>
          </motion.div>

          {/* Future You Preview - Weight Loss Timeline */}
          <FutureYouPreview quizResponses={quizResponses} />

          {/* Recommended Path */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="backdrop-blur-xl bg-slate-950/40 border border-white/10 rounded-2xl p-8"
            data-testid="recommended-path-card"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#22D3EE]/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-6 w-6 text-[#22D3EE]" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold mb-3">Recommended Path</h2>
                <p className="text-slate-300 leading-relaxed">{forecastData.recommended_path}</p>
              </div>
            </div>
          </motion.div>

          {/* Based on Your Profile */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.42 }}
            className="p-6 rounded-2xl bg-white/5 backdrop-blur border border-white/10"
            data-testid="profile-summary-card"
          >
            <h3 className="text-xl font-semibold text-white mb-3">
              Based on Your Profile
            </h3>

            <p className="text-slate-300 mb-4">
              Based on your responses{profile?.first_name ? `, ${profile.first_name},` : ','}{" "}
              individuals with similar goals and timelines often explore provider-guided{" "}
              {profile?.recommended_medication ? profile.recommended_medication : "GLP-1"}{" "}
              programs with personalized adjustments over time.
            </p>

            <p className="text-slate-400 mb-4">
              {profile?.weight_to_lose && profile?.timeline
                ? `Your goal to lose ${profile.weight_to_lose} lbs in ${profile.timeline} suggests you may benefit from a structured, medically guided approach tailored to your preferences and experience level.`
                : "Your responses suggest you may benefit from a structured, medically guided approach tailored to your preferences and experience level."}
            </p>

            <p className="text-xs text-slate-500">
              Results vary. All treatment options require evaluation and approval by a licensed medical provider.
            </p>
          </motion.div>

          {/* Your Results Will Be Reviewed */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.44 }}
            className="p-6 rounded-2xl bg-cyan-500/10 border border-cyan-400/20"
            data-testid="results-reviewed-card"
          >
            <h3 className="text-lg font-semibold text-cyan-300 mb-2">
              Your Results Will Be Reviewed
            </h3>

            <p className="text-slate-300 mb-3">
              Bryan personally reviews your profile to help match you with the most appropriate option based on your goals, timeline, and experience.
            </p>

            <p className="text-slate-400 text-sm">
              You'll receive guidance tailored to your situation — not a one-size-fits-all recommendation.
            </p>

            <p className="text-xs text-slate-500 mt-2">
              Results vary. All treatment decisions require consultation with a licensed provider.
            </p>
          </motion.div>

          {/* Primary CTA - Right after Recommended Path */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="backdrop-blur-xl bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 border-2 border-emerald-500/30 rounded-2xl p-8 shadow-[0_0_30px_rgba(16,185,129,0.2)]"
            data-testid="primary-cta-section"
          >
            <div className="text-center">
              <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full border border-emerald-500/20 bg-emerald-500/5">
                <Zap className="h-4 w-4 text-emerald-400" />
                <span className="text-emerald-400 text-sm font-medium">READY TO START?</span>
              </div>
              
              <h3 className="font-heading text-2xl sm:text-3xl font-bold mb-3 text-white">
                Take the Next Step
              </h3>
              <p className="text-slate-400 mb-6 max-w-lg mx-auto">
                Connect with Bryan to review your forecast and explore treatment options that fit your goals.
              </p>
              
              <div className="flex flex-col items-center gap-4">
                <a
                  href="https://calendly.com/bryanm8623/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white rounded-full px-12 py-7 text-xl font-bold shadow-[0_0_30px_rgba(16,185,129,0.5)] hover:shadow-[0_0_40px_rgba(16,185,129,0.7)] transition-all hover:scale-105 w-full sm:w-auto"
                  data-testid="primary-cta-schedule"
                >
                  <Calendar className="mr-3 h-6 w-6" />
                  Book My Free Call
                </a>
                
                <p className="text-slate-400 text-base">
                  Or text Bryan directly at <a href="sms:8016518363" className="text-cyan-400 hover:text-cyan-300 font-semibold">801-651-8363</a>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* AI Suggested Path Section */}
      {medicationRec && (
        <section className="py-16 border-t border-white/5">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full border border-cyan-500/20 bg-cyan-500/5 backdrop-blur-sm">
                  <Brain className="h-4 w-4 text-[#22D3EE]" />
                  <span className="text-[#22D3EE] text-sm font-medium tracking-wide">PERSONALIZED RECOMMENDATION</span>
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">
                  AI Suggested Path
                </h2>
                <p className="text-slate-400 text-lg">
                  Based on your profile, here are the options that may align best with your goals.
                </p>
              </div>

              {/* Medication Options */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* Primary Option */}
                <div className="backdrop-blur-xl bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border-2 border-cyan-500/30 rounded-2xl p-8 shadow-[0_0_30px_rgba(34,211,238,0.2)] relative overflow-hidden">
                  <div className="absolute top-4 right-4 px-3 py-1 bg-cyan-500/20 border border-cyan-500/30 rounded-full">
                    <span className="text-xs font-bold text-cyan-300">PRIMARY</span>
                  </div>
                  
                  <div className="w-14 h-14 rounded-xl bg-cyan-500/20 flex items-center justify-center mb-6 shadow-[0_0_20px_rgba(34,211,238,0.3)]">
                    <Target className="h-7 w-7 text-[#22D3EE]" />
                  </div>
                  
                  <h3 className="font-heading text-2xl font-bold mb-3 text-white">
                    {medicationRec.primary}
                  </h3>
                  
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    {medicationRec.primary === 'Tirzepatide' 
                      ? 'Dual-action medication targeting GLP-1 and GIP receptors for comprehensive metabolic support.'
                      : 'Well-established GLP-1 medication with proven support for appetite regulation and weight management.'
                    }
                  </p>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-400"></div>
                      <span className="text-slate-400">Provider-supervised treatment</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-400"></div>
                      <span className="text-slate-400">Personalized dosing</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-400"></div>
                      <span className="text-slate-400">Ongoing support & monitoring</span>
                    </div>
                  </div>
                </div>

                {/* Alternative Option */}
                <div className="backdrop-blur-xl bg-slate-950/40 border border-slate-800 rounded-2xl p-8 hover:border-cyan-500/20 transition-all">
                  <div className="absolute top-4 right-4 px-3 py-1 bg-slate-800/60 border border-slate-700 rounded-full">
                    <span className="text-xs font-bold text-slate-400">ALTERNATIVE</span>
                  </div>
                  
                  <div className="w-14 h-14 rounded-xl bg-slate-800/60 flex items-center justify-center mb-6">
                    <Activity className="h-7 w-7 text-slate-400" />
                  </div>
                  
                  <h3 className="font-heading text-2xl font-bold mb-3 text-white">
                    {medicationRec.alternative}
                  </h3>
                  
                  <p className="text-slate-400 text-sm leading-relaxed mb-4">
                    {medicationRec.alternative === 'Tirzepatide'
                      ? 'Dual-action medication targeting GLP-1 and GIP receptors for comprehensive metabolic support.'
                      : 'Well-established GLP-1 medication with proven support for appetite regulation and weight management.'
                    }
                  </p>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-500"></div>
                      <span className="text-slate-500">Also available with provider approval</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-500"></div>
                      <span className="text-slate-500">Flexible treatment options</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reasoning */}
              <div className="backdrop-blur-xl bg-slate-950/40 border border-cyan-500/10 rounded-xl p-6 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <Brain className="h-4 w-4 text-[#22D3EE]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-2">Why This Recommendation?</h4>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      {medicationRec.reasoning}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Options */}
              <div className="text-center">
                <h3 className="font-heading text-2xl font-bold mb-2">Review Your Options With Bryan</h3>
                <p className="text-slate-400 mb-8">Choose the best way to connect</p>
                
                {/* Primary CTA */}
                <div className="mb-8">
                  <a
                    href="https://calendly.com/bryanm8623/30min"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white rounded-full px-12 py-7 text-xl font-bold shadow-[0_0_30px_rgba(16,185,129,0.5)] hover:shadow-[0_0_40px_rgba(16,185,129,0.7)] transition-all hover:scale-105"
                    data-testid="review-options-book-call"
                  >
                    <Calendar className="mr-3 h-6 w-6" />
                    Book My Free Call
                  </a>
                  <p className="text-slate-400 text-base mt-4">
                    Or text Bryan directly at <a href="sms:8016518363" className="text-cyan-400 hover:text-cyan-300 font-semibold">801-651-8363</a>
                  </p>
                </div>

                {/* Three Contact Buttons - Secondary Options */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto mb-6">
                  {/* Text Bryan Now */}
                  <a
                    href="sms:8016518363"
                    className="backdrop-blur-xl bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border-2 border-cyan-500/30 rounded-xl p-6 hover:border-cyan-500/50 hover:bg-cyan-500/15 transition-all group text-center block"
                    data-testid="text-bryan-btn"
                  >
                    <div className="w-12 h-12 mx-auto rounded-full bg-cyan-500/20 flex items-center justify-center mb-4 group-hover:bg-cyan-500/30 transition-all">
                      <MessageSquare className="h-6 w-6 text-[#22D3EE]" />
                    </div>
                    <h4 className="font-bold text-white mb-2">Text Bryan Now</h4>
                    <p className="text-slate-400 text-sm mb-3">Get a quick response via SMS</p>
                    <div className="text-cyan-400 text-xs font-mono">801-651-8363</div>
                  </a>

                  {/* Schedule a Quick Call */}
                  <a
                    href="https://calendly.com/bryanm8623/30min"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="backdrop-blur-xl bg-slate-950/40 border border-slate-800 rounded-xl p-6 hover:border-cyan-500/30 hover:bg-slate-900/60 transition-all group text-center block"
                    data-testid="schedule-call-btn"
                  >
                    <div className="w-12 h-12 mx-auto rounded-full bg-slate-800/60 flex items-center justify-center mb-4 group-hover:bg-cyan-500/20 transition-all">
                      <Calendar className="h-6 w-6 text-slate-400 group-hover:text-[#22D3EE] transition-colors" />
                    </div>
                    <h4 className="font-bold text-white mb-2">Schedule a Quick Call</h4>
                    <p className="text-slate-400 text-sm mb-3">Book a convenient time</p>
                    <div className="text-emerald-500 text-xs font-medium">Book Now on Calendly</div>
                  </a>

                  {/* Email Bryan */}
                  <a
                    href="mailto:Bryan@DirectMeds.com"
                    className="backdrop-blur-xl bg-slate-950/40 border border-slate-800 rounded-xl p-6 hover:border-cyan-500/30 hover:bg-slate-900/60 transition-all group text-center block"
                    data-testid="email-bryan-btn"
                  >
                    <div className="w-12 h-12 mx-auto rounded-full bg-slate-800/60 flex items-center justify-center mb-4 group-hover:bg-cyan-500/20 transition-all">
                      <Mail className="h-6 w-6 text-slate-400 group-hover:text-[#22D3EE] transition-colors" />
                    </div>
                    <h4 className="font-bold text-white mb-2">Email Bryan</h4>
                    <p className="text-slate-400 text-sm mb-3">Send a detailed message</p>
                    <div className="text-slate-500 text-xs break-all">Bryan@DirectMeds.com</div>
                  </a>
                </div>

                <p className="text-slate-500 text-sm mb-2">
                  Bryan typically responds quickly during business hours.
                </p>
                
                <p className="text-slate-600 text-xs">
                  All medication options require provider review and medical eligibility approval
                </p>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Recommended Next Step Section */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full border border-cyan-500/20 bg-cyan-500/5 backdrop-blur-sm">
              <Brain className="h-4 w-4 text-[#22D3EE]" />
              <span className="text-[#22D3EE] text-sm font-medium tracking-wide">YOUR NEXT STEP</span>
            </div>

            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Recommended Next Step
            </h2>
            
            <p className="text-slate-300 text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
              Based on your personalized forecast, Bryan can help you explore the treatment options 
              that align best with your goals and preferences.
            </p>

            <div className="flex flex-col items-center gap-4">
              <a
                href="https://calendly.com/bryanm8623/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white rounded-full px-12 py-7 text-xl font-bold shadow-[0_0_30px_rgba(16,185,129,0.5)] hover:shadow-[0_0_40px_rgba(16,185,129,0.7)] transition-all hover:scale-105"
                data-testid="talk-to-bryan-cta"
              >
                <Calendar className="mr-3 h-6 w-6" />
                Book My Free Call
              </a>
              
              <p className="text-slate-400 text-base">
                Or text Bryan directly at <a href="sms:8016518363" className="text-cyan-400 hover:text-cyan-300 font-semibold">801-651-8363</a>
              </p>
              
              <Button
                onClick={() => openLearnMore('general')}
                variant="outline"
                className="border-cyan-500/30 hover:border-cyan-500/50 bg-cyan-500/5 hover:bg-cyan-500/10 text-cyan-300 hover:text-cyan-200 rounded-full px-10 py-6 text-lg font-bold transition-all mt-2"
                data-testid="see-options-cta"
              >
                <Target className="mr-2 h-5 w-5" />
                See My Options
              </Button>
            </div>

            <p className="text-slate-500 text-sm mt-8 max-w-xl mx-auto">
              Our team typically responds within 24 hours to discuss your forecast and help you 
              understand which paths may be the best fit for your situation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Advanced Metabolic Support Section */}
      {shouldShowSermorelin() && (
        <section className="py-16 border-t border-white/5">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full border border-purple-500/20 bg-purple-500/5 backdrop-blur-sm">
                  <Sparkles className="h-4 w-4 text-purple-400" />
                  <span className="text-purple-400 text-sm font-medium tracking-wide">ADVANCED METABOLIC SUPPORT</span>
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">
                  Optimize Your Results
                </h2>
                <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                  Based on your profile, you may benefit from advanced metabolic optimization options.
                </p>
              </div>

              {/* Sermorelin Card */}
              <div className="backdrop-blur-xl bg-gradient-to-br from-slate-900/60 to-purple-950/20 border border-purple-500/20 rounded-2xl p-8 shadow-[0_0_30px_rgba(168,85,247,0.15)] mb-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(168,85,247,0.3)]">
                    <Zap className="h-7 w-7 text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-2xl font-bold mb-2 text-white">
                      Sermorelin (Recovery & Metabolic Support)
                    </h3>
                    <p className="text-purple-300/70 text-sm font-medium uppercase tracking-wide mb-3">
                      Advanced Peptide Therapy
                    </p>
                  </div>
                </div>

                <div className="space-y-4 mb-8">
                  <p className="text-slate-300 leading-relaxed">
                    Sermorelin is designed to support the body's natural growth hormone signaling pathways. 
                    Many individuals exploring metabolic optimization consider Sermorelin to help support recovery, 
                    sleep quality, and long-term body composition goals.
                  </p>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    This peptide therapy may help individuals who are looking to optimize energy levels, enhance 
                    physical recovery, and support overall metabolic health as part of a comprehensive wellness plan.
                  </p>
                </div>

                {/* Three Info Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                  <div className="bg-slate-900/60 border border-purple-500/10 rounded-xl p-5 hover:border-purple-500/30 transition-all">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center mb-3">
                      <Moon className="h-5 w-5 text-purple-400" />
                    </div>
                    <h4 className="font-heading font-bold text-white mb-2">Recovery Support</h4>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      May support physical recovery and sleep quality.
                    </p>
                  </div>

                  <div className="bg-slate-900/60 border border-purple-500/10 rounded-xl p-5 hover:border-purple-500/30 transition-all">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center mb-3">
                      <Zap className="h-5 w-5 text-purple-400" />
                    </div>
                    <h4 className="font-heading font-bold text-white mb-2">Energy Optimization</h4>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      May support improved daily energy levels.
                    </p>
                  </div>

                  <div className="bg-slate-900/60 border border-purple-500/10 rounded-xl p-5 hover:border-purple-500/30 transition-all">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center mb-3">
                      <Activity className="h-5 w-5 text-purple-400" />
                    </div>
                    <h4 className="font-heading font-bold text-white mb-2">Body Composition</h4>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      May support metabolic health and body composition over time.
                    </p>
                  </div>
                </div>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    onClick={() => openLearnMore('sermorelin')}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-full px-6 py-6 text-base font-bold shadow-[0_0_20px_rgba(168,85,247,0.4)] hover:shadow-[0_0_30px_rgba(168,85,247,0.6)] transition-all"
                    data-testid="learn-sermorelin-btn"
                  >
                    Learn About Sermorelin
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  
                  <Button
                    onClick={() => setShowContactModal(true)}
                    variant="outline"
                    className="flex-1 border-purple-500/30 hover:border-purple-500/50 bg-purple-500/5 hover:bg-purple-500/10 text-purple-300 hover:text-purple-200 rounded-full px-6 py-6 text-base font-bold transition-all"
                    data-testid="talk-to-bryan-btn"
                  >
                    Talk to Bryan About Options
                  </Button>
                </div>

                {/* Compliance Note */}
                <div className="mt-6 pt-6 border-t border-purple-500/10">
                  <p className="text-xs text-slate-500 leading-relaxed">
                    <strong className="text-slate-400">Important:</strong> Sermorelin is not FDA approved for weight loss. 
                    All treatment decisions require provider review and medical eligibility assessment. Individual results 
                    may vary. This information is for educational purposes only and does not constitute medical advice.
                  </p>
                </div>
              </div>

              {/* Future Expansion Placeholder - Structured for additional therapies */}
              <div className="text-center">
                <p className="text-slate-500 text-sm">
                  Additional metabolic optimization programs available upon consultation.
                </p>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h2 className="font-heading text-3xl font-bold mb-4">Ready to Take Action?</h2>
            <p className="text-slate-400 mb-8">
              Book your free call with Bryan to review your forecast and explore your options.
            </p>
            
            <div className="flex flex-col items-center gap-4 mb-8">
              <a
                href="https://calendly.com/bryanm8623/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white rounded-full px-12 py-7 text-xl font-bold shadow-[0_0_30px_rgba(16,185,129,0.5)] hover:shadow-[0_0_40px_rgba(16,185,129,0.7)] transition-all hover:scale-105"
                data-testid="final-cta-book-call"
              >
                <Calendar className="mr-3 h-6 w-6" />
                Book My Free Call
              </a>
              
              <p className="text-slate-400 text-base">
                Or text Bryan directly at <a href="sms:8016518363" className="text-cyan-400 hover:text-cyan-300 font-semibold">801-651-8363</a>
              </p>
            </div>
            
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-slate-400 hover:text-white rounded-full px-8 py-4"
              data-testid="back-home-btn"
            >
              Return Home
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>

            {/* Bonus Starter Kit */}
            <div className="mt-8 p-6 rounded-2xl bg-[#0B1F3A] border border-[#2ABFBF]/20 text-center" data-testid="bonus-starter-kit">
              <p className="text-[#2ABFBF] font-semibold mb-2">As a bonus — grab your free GLP-1 Starter Kit</p>
              <p className="text-slate-400 text-sm mb-4">11-page guide with meal plans, side effect tips, tracker, and more.</p>
              <Link
                to="/free-starter-kit"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2ABFBF] text-[#0B1F3A] font-bold text-sm hover:bg-[#24a8a8] transition-colors"
              >
                <Download className="h-4 w-4" />
                Get the Free Kit →
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center text-sm text-slate-500 space-y-2">
            <p className="font-medium">IMPORTANT DISCLAIMER</p>
            <p>
              This forecast is for informational purposes only. All treatment options require provider review and approval.
              Compounded medications are not FDA approved. Results vary by individual.
            </p>
          </div>
        </div>
      </section>

      {/* Modals */}
      <ScheduleModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        source="results_page"
        leadId={leadId}
      />
      
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        source="results_page"
        leadInfo={quizResponses ? {
          first_name: quizResponses.first_name,
          email: quizResponses.email,
          phone: quizResponses.phone,
          id: leadId
        } : null}
      />
      
      <LearnMoreModal
        isOpen={showLearnMoreModal}
        onClose={() => setShowLearnMoreModal(false)}
        topic={learnMoreTopic}
        source="results_page"
        leadId={leadId}
      />
    </div>
  );
};

export default ResultsPage;